import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { MailCheck } from 'lucide-react';
import * as React from 'react';

import { useZone } from '@/design';
import { useAuth } from '../auth-context';
import { verificationPromptStorage } from '../storage';
import { VerificationRequest } from './verification-request';

/**
 * The prompt to confirm an address — under the header, on every screen of the portal, while
 * `/auth/me` says `emailVerified: false` (survey-api's self-service plan, R16).
 *
 * **A prompt, never a gate** (R15). An unconfirmed address blocks neither sign-in nor a survey:
 * access for 45 336 migrated people already rests on mail, and a second wall on the same
 * channel would double its one point of failure. So nothing here stops anything, the copy says
 * nothing waits on it, and "Not now" puts it away for the rest of the tab's session
 * (`verificationPromptStorage`) — the owner's ruling: on every screen, and dismissible.
 *
 * **It goes when the address is confirmed, in whichever tab.** The link is usually opened in a
 * new tab, and redeeming it touches no session, so nothing tells this one — the prompt re-reads
 * `/auth/me` each time the tab is shown again, while it is showing. One GET per return to the
 * tab, and only for an account that still has something to confirm.
 */
export function EmailVerificationBanner() {
  const zone = useZone();
  const { user, refreshUser } = useAuth();
  const [dismissedFor, setDismissedFor] = React.useState<Nullable<string>>(null);

  // `=== false`, not falsy: an account read from a survey-api without the member is not one
  // to prompt, and its "Send a new link" would be a route that is not there.
  const shown =
    !!user &&
    user.emailVerified === false &&
    dismissedFor !== user.accountId &&
    !verificationPromptStorage.isDismissed(user.accountId);

  React.useEffect(() => {
    if (!shown) return undefined;
    const reread = () => {
      if (document.visibilityState === 'visible') void refreshUser();
    };
    document.addEventListener('visibilitychange', reread);
    return () => document.removeEventListener('visibilitychange', reread);
  }, [shown, refreshUser]);

  if (!shown || !user) return null;

  const address = <strong className="break-words">{user.email}</strong>;

  const dismiss = () => {
    verificationPromptStorage.dismiss(user.accountId);
    setDismissedFor(user.accountId);
  };

  // The same prompt in both portals, at each one's density: the Advisor Portal's reading column,
  // or one tight band across the Team Portal's working area.
  const dense = zone === 'team';

  return (
    <section
      aria-label="Email confirmation"
      className={cn(
        'border-b border-border bg-[var(--color-slight-blue)]/40',
        dense ? 'px-6 py-2' : 'px-6 py-3 lg:px-10'
      )}
    >
      <div className={cn('flex items-start gap-3', !dense && 'mx-auto max-w-[1360px]')}>
        <MailCheck
          className={cn(
            'shrink-0 text-[var(--color-royal-blue)]',
            dense ? 'mt-0.5 size-4' : 'mt-0.5 size-5'
          )}
          aria-hidden
        />
        <div
          className={cn(
            'min-w-0 flex-1',
            dense ? 'flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[0.8125rem]' : 'grid gap-2 text-sm'
          )}
        >
          <p className="font-medium text-[var(--color-royal-blue)]">Confirm your email address</p>
          <p className={cn('text-muted-foreground', dense && 'basis-full xl:basis-auto')}>
            We have not yet confirmed that {address} is yours. The link we mail there does it.
            Nothing waits on it — everything here works either way.
          </p>
          <VerificationRequest email={user.email} />
        </div>
        <Button type="button" variant="ghost" size="sm" className="shrink-0" onClick={dismiss}>
          Not now
        </Button>
      </div>
    </section>
  );
}
